import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import Spinner from '../ogComponent/spinner';
import { IconDownload, IconTrash } from '@tabler/icons-react';
import axios from 'axios';
import { toast } from 'react-toastify';

export default function SalarySlip() {
    const [selectedYear, setSelectedYear] = useState(null);
    const [salaryData, setSalaryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const navigate = useNavigate();
    const { emp_id } = useParams();

    // Access permissions array from Redux store and userId from localStorage
    const permissions = useSelector((state) => state.emp.permissions);
    const userId = localStorage.getItem("userId");

    const pagePermissions = ["listsalaryslipID", "showsalaryslipID", "downloadsalaryslipID", "deletesalaryslipID"];

    // Get current year and set as initial value
    const currentYear = new Date().getFullYear();

    // Generate year options
    const yearOptions = [];
    for (let i = currentYear; i >= 2000; i--) {
        yearOptions.push({ value: i, label: i });
    }

    // Set initial year selection
    useEffect(() => {
        setSelectedYear({ value: currentYear, label: currentYear });
    }, []);

    // Fetch salary slip data when year changes or component mounts
    useEffect(() => {
        if (selectedYear?.value && (permissions.includes("listsalaryslipID") || String(emp_id) === String(userId))) {
            fetchSalarySlips();
        } else {
            setLoading(false);
        }
    }, [selectedYear, emp_id, userId, permissions]);

    const fetchSalarySlips = async () => {
        try {
            setLoading(true);
            const accessToken = localStorage.getItem('accessToken');
            const response = await fetch(
                `${process.env.REACT_APP_API}/api/attendance/salary-slip-list/?employee_id=${emp_id}&year=${selectedYear.value}`,
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const data = await response.json();
            if (data.success) {
                setSalaryData(data.data);
            } else {
                toast.error('Failed to load salary slips');
            }
        } catch (error) {
            console.error('Error fetching salary slips:', error);
            toast.error('Failed to load salary slips');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (slipId, event) => {
        event.stopPropagation();
        try {
            setDownloadingId(slipId);
            const accessToken = localStorage.getItem('accessToken');

            const response = await axios.get(
                `${process.env.REACT_APP_API}/api/attendance/download-salary-slip-pdf/${slipId}/`,
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    responseType: 'blob'
                }
            );

            const slip = salaryData.find(s => s.id === slipId);
            const monthName = slip ? getMonthName(slip.pay_month) : '';
            const year = slip ? new Date(slip.pay_month).getFullYear() : '';

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `salary_slip_${monthName}_${year}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success('Salary slip downloaded successfully');
        } catch (error) {
            console.error('Error downloading salary slip:', error);
            toast.error('Failed to download salary slip');
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDelete = async (slipId, event) => {
        event.stopPropagation();
        try {
            setDeletingId(slipId);
            const accessToken = localStorage.getItem('accessToken');

            await axios.delete(
                `${process.env.REACT_APP_API}/api/attendance/salary-slip-delete/${slipId}/`,
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    }
                }
            );

            toast.success('Salary slip deleted successfully');
            fetchSalarySlips(); // Reload salary slips after deletion
        } catch (error) {
            console.error('Error deleting salary slip:', error);
            toast.error('Failed to delete salary slip');
        } finally {
            setDeletingId(null);
        }
    };

    const handleYearChange = (selectedOption) => {
        setSelectedYear(selectedOption);
    };

    const singleSlipView = (id) => {
        if (permissions.includes("showsalaryslipID") || String(emp_id) === String(userId)) {
            navigate(`/user/salaryslip/${emp_id}/${id}`);
        }
    };

    const getMonthName = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('default', { month: 'long' });
    };

    const canListSalarySlips = permissions.includes("listsalaryslipID") || String(emp_id) === String(userId);

    return (
        <>
            <div className='px-4 py-4 w-100'>
                <div className='mb-3'>
                    <h4>Salary Slip</h4>
                </div>
                <div style={{ width: '200px' }}>
                    <Select
                        id="salary-year"
                        value={selectedYear}
                        onChange={handleYearChange}
                        options={yearOptions}
                        placeholder="Select Year"
                        className="border rounded"
                        style={{ width: '200px' }}
                        isDisabled={!canListSalarySlips}
                    />
                </div>

                <div className="slipCardContainer w-100 mt-4">
                    {loading ? (
                        <Spinner />
                    ) : canListSalarySlips ? (
                        salaryData.length > 0 ? (
                            <div className="row">
                                {salaryData.map((slip) => (
                                    <div key={slip.id} className="col-12 col-sm-6 col-md-4 col-lg-3 col-xxl-2 mb-4">
                                        <div className="card" style={{ height: '320px' }}>
                                            <div
                                                className="card-body h-100 d-flex justify-content-center align-items-center flex-column position-relative"
                                                style={{
                                                    height: '100%',
                                                    cursor: (permissions.includes("showsalaryslipID") || String(emp_id) === String(userId)) ? 'pointer' : 'default',
                                                }}
                                                onClick={() => singleSlipView(slip.id)}
                                            >
                                                <div
                                                    className="position-absolute top-0 start-0 w-100 h-100"
                                                    style={{
                                                        background: 'url(/assets/certificate.jpg) no-repeat center center',
                                                        backgroundSize: 'cover',
                                                        filter: 'blur(1px)',
                                                        zIndex: 1,
                                                    }}
                                                ></div>
                                                <div className="position-absolute top-0 end-0 p-2" style={{ zIndex: 10000 }}>
                                                    {(permissions.includes("deletesalaryslipID") || String(emp_id) === String(userId)) && (
                                                        <button
                                                            className="btn btn-danger btn-sm"
                                                            onClick={(e) => handleDelete(slip.id, e)}
                                                            disabled={deletingId === slip.id}
                                                        >
                                                            {deletingId === slip.id ? 'Deleting...' : <IconTrash stroke={2} size={20} />}
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="position-relative d-flex justify-content-center align-items-center flex-column h-100 w-100"
                                                    style={{ zIndex: 2 }}
                                                >
                                                    <h5 className="card-title mb-3 text-black" style={{ textShadow: '1px 1px 2px rgba(255, 255, 255, 0.5)' }}>
                                                        {getMonthName(slip.pay_month)}
                                                    </h5>
                                                    {(permissions.includes("downloadsalaryslipID") || String(emp_id) === String(userId)) && (
                                                        <button
                                                            className="btn text-white"
                                                            style={{
                                                                background: '#d29729',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                            }}
                                                            onClick={(e) => handleDownload(slip.id, e)}
                                                            disabled={downloadingId === slip.id}
                                                        >
                                                            {downloadingId === slip.id ? 'Downloading...' : 'Download'}
                                                            <IconDownload stroke={2} size={20} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div
                                className="d-flex justify-content-center align-items-end position-relative"
                                style={{ height: "50vh" }}
                            >
                                <div
                                    className="position-absolute top-0 start-0 w-100 h-100"
                                    style={{
                                        backgroundImage: "url(/assets/file.png)",
                                        backgroundSize: "contain",
                                        backgroundPosition: "center",
                                        backgroundRepeat: "no-repeat",
                                        opacity: 0.3,
                                    }}
                                ></div>
                                <p className="m-0 fw-light fs-5 position-relative">Salary Slips Not Available!</p>
                            </div>
                        )
                    ) : (
                        <p>No permission to view salary slips.</p>
                    )}
                </div>
            </div>
        </>
    );
}